"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBanner, StatusBannerRow, StatusBannerContent, StatusBannerActions, StatusBannerIconButton, STATUS_BANNER_PRESS, statusBannerColorMap } from "@/components/ui/status-banner";
import { useOrgLockoutStatus, useNotifyBillingAdmin } from "@/entities/billing/model";
import type { LockoutStatusResponse } from "@/contexts/billing/core/presentation/rpc/billing-lockout-rpc";
import { formatBillingCurrency } from "./billing-helpers";
interface SoftLockBannerProps {
  className?: string;
  /** Pre-fetched lockout status from BillingGate. When provided, skips the
   *  separate useOrgLockoutStatus() RPC call to avoid a double-fetch race. */
  lockoutStatus?: LockoutStatusResponse | null;
}

/**
 * Persistent amber banner shown when the org is in soft_lock or warning/escalating phase.
 * Collapsible to a thin stripe but not fully dismissible.
 *
 * - Admins see "Update Payment Method" link
 * - Non-admins see "Notify [Admin Name]" button
 */
export function SoftLockBanner(t0) {
  const $ = _c(4);
  const {
    className,
    lockoutStatus
  } = t0;
  const {
    data: fetchedStatus,
    isLoading
  } = useOrgLockoutStatus(!lockoutStatus);
  const status = lockoutStatus ?? fetchedStatus;
  const [isCollapsed, setIsCollapsed] = useState(false);
  if (!lockoutStatus && (isLoading || !status || !status.phase)) {
    return null;
  }
  if (!status?.phase) {
    return null;
  }
  if (status.phase === "hard_lock") {
    return null;
  }
  let t1;
  if ($[0] !== className || $[1] !== isCollapsed || $[2] !== status) {
    t1 = isCollapsed ? <CollapsedBanner status={status} onClick={() => setIsCollapsed(false)} className={className} /> : <ExpandedBanner status={status} onCollapse={() => setIsCollapsed(true)} className={className} />;
    $[0] = className;
    $[1] = isCollapsed;
    $[2] = status;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function getCollapsedText(status: LockoutStatusResponse): string {
  if (status.amountDueCents != null && status.currency) {
    return `Payment of ${formatBillingCurrency(status.amountDueCents, status.currency)} failed - features restricted`;
  }
  return "Payment issue - features restricted";
}
function CollapsedBanner(t0) {
  const $ = _c(12);
  const {
    status,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertTriangle className="h-3.5 w-3.5" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== status) {
    t2 = getCollapsedText(status);
    $[1] = status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span>{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ChevronDown className="h-3.5 w-3.5 ml-1" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onClick || $[7] !== t3) {
    t5 = <button type="button" onClick={onClick} className={`flex w-full items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors cursor-pointer ${STATUS_BANNER_PRESS}`}>{t1}{t3}{t4}</button>;
    $[6] = onClick;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== className || $[10] !== t5) {
    t6 = <StatusBanner color="amber" className={className}>{t5}</StatusBanner>;
    $[9] = className;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function getAdminMessage(status: LockoutStatusResponse): string {
  if (status.paymentMethodBrand && status.paymentMethodLast4) {
    if (status.amountDueCents != null && status.currency) {
      return `Payment of ${formatBillingCurrency(status.amountDueCents, status.currency)} failed on your ${status.paymentMethodBrand} ending in ${status.paymentMethodLast4}`;
    }
    return `Your ${status.paymentMethodBrand} ending in ${status.paymentMethodLast4} needs to be updated`;
  }
  if (status.amountDueCents != null && status.currency) {
    return `Payment of ${formatBillingCurrency(status.amountDueCents, status.currency)} failed`;
  }
  return "There's a billing issue that needs your attention";
}
function ExpandedBanner(t0) {
  const $ = _c(20);
  const {
    status,
    onCollapse,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertTriangle className="h-5 w-5 text-white/80 mt-0.5 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== status) {
    t2 = status.isAdmin ? getAdminMessage(status) : "Some features are temporarily restricted due to a billing issue";
    $[1] = status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <p className="text-sm font-medium text-white">{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== status.billingAdminName || $[6] !== status.isAdmin) {
    t4 = <div className="mt-2">{status.isAdmin ? <AdminActions /> : <MemberActions adminName={status.billingAdminName} />}</div>;
    $[5] = status.billingAdminName;
    $[6] = status.isAdmin;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <StatusBannerContent className="flex items-start gap-3">{t1}<div className="flex-1">{t3}{t4}</div></StatusBannerContent>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ChevronUp className="h-4 w-4" />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onCollapse) {
    t7 = <StatusBannerActions><StatusBannerIconButton color="amber" onClick={onCollapse} aria-label="Collapse banner">{t6}</StatusBannerIconButton></StatusBannerActions>;
    $[12] = onCollapse;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <StatusBannerRow>{t5}{t7}</StatusBannerRow>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== className || $[18] !== t8) {
    t9 = <StatusBanner color="amber" className={className}>{t8}</StatusBanner>;
    $[17] = className;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function AdminActions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <a href="/settings/billing" className="inline-flex items-center text-sm font-medium underline underline-offset-2">Update Payment Method</a>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MemberActions(t0) {
  const $ = _c(12);
  const {
    adminName
  } = t0;
  const {
    mutate: notifyAdmin,
    isPending,
    data
  } = useNotifyBillingAdmin();
  let t1;
  if ($[0] !== notifyAdmin) {
    t1 = () => {
      notifyAdmin();
    };
    $[0] = notifyAdmin;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleNotify = t1;
  const wasRateLimited = data?.rateLimited === true;
  const wasSent = data?.success === true;
  const wasSuppressed = data?.suppressed === true;
  const t2 = isPending || wasSent || wasRateLimited || wasSuppressed;
  const t3 = isPending ? "Sending..." : wasSent ? "Notified" : wasRateLimited ? "Already notified" : wasSuppressed ? "Notifications muted" : `Notify ${adminName ?? "billing admin"}`;
  let t4;
  if ($[2] !== handleNotify || $[3] !== t2 || $[4] !== t3) {
    t4 = <Button variant="outline" size="sm" onClick={handleNotify} disabled={t2} className={`h-7 text-xs ${statusBannerColorMap.amber.actionButton}`}>{t3}</Button>;
    $[2] = handleNotify;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== wasSuppressed) {
    t5 = wasSuppressed && <span className="text-xs">They have muted these alerts</span>;
    $[6] = wasSuppressed;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <a href="/settings/billing" className="text-xs underline underline-offset-2">View details</a>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t5) {
    t7 = <div className="flex items-center gap-3">{t4}{t5}{t6}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}